import {
  CompetencyMapQuery,
  Grade,
  Language,
  Question_Type,
  useBatchInitGenerateQuestionsMutation,
  useQuestionSubtypesQuery,
} from "@generated/graphql";
import { Dialog, DialogContent, Grid, Stack } from "@mui/material";
import CircularLoading from "components/CircularLoading";
import Form from "components/form/Form";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { CompetencyMapSelectedParts } from "helpers/competencyMapSelectParts";
import { parseApolloError } from "helpers/errorsParser";
import { RefetchCompetencyMapGenQuestionsLog } from "pages/CompetencyMaps/view/components/types";
import { useContext, useMemo } from "react";
import { useForm } from "react-hook-form";
import palette from "theme/palette";
import ModalFooter from "./components/ModalFooter";
import ModalHeader from "./components/ModalHeader";
import PromptSection from "./components/PromptSection";
import QuestionTypesSection from "./components/QuestionTypesSection";
import ScopeSection from "./components/ScopeSection";
import SettingsSection from "./components/SettingsSection";
import TotalQuestionsCard from "./components/TotalQuestionsCard";
import { GenerateQuestionsForCMModalForm } from "./types";
import {
  getFooterMetaText,
  getQuestionSubtypesData,
  getQuestionSubtypesOptions,
  getScopeAreasCount,
  getScopeGroups,
  getTotalQuestionsCount,
  GRADE_DESCRIPTIONS,
} from "./utils";

export type GenerateQuestionsForCMModalProps = {
  competencyMap: CompetencyMapQuery["competencyMap"];
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  refetchCompetencyMapGenQuestionsLog: RefetchCompetencyMapGenQuestionsLog;
  selectParts?: CompetencyMapSelectedParts;
  hideScopeAndPrompts?: boolean;
};

const getGradeButtonColors = (grade: Grade, isActive: boolean) => {
  if (!isActive) {
    return {
      borderColor: palette.divider,
      backgroundColor: "common.white",
      color: "text.secondary",
    };
  }

  if (grade === Grade.Junior) {
    return {
      borderColor: palette.green.contrast,
      backgroundColor: palette.green.light,
      color: palette.green.contrast,
    };
  }

  if (grade === Grade.Middle) {
    return {
      borderColor: palette.orange.contrast,
      backgroundColor: palette.orange.light,
      color: palette.orange.contrast,
    };
  }

  return {
    borderColor: palette.error.main,
    backgroundColor: palette.red.light,
    color: palette.error.main,
  };
};

const GenerateQuestionsForCMModal = ({
  competencyMap,
  open,
  onClose,
  onSuccess,
  refetchCompetencyMapGenQuestionsLog,
  selectParts,
  hideScopeAndPrompts = false,
}: GenerateQuestionsForCMModalProps) => {
  const { addToast } = useContext(ToastContext);
  const [generateQuestions, { loading }] = useBatchInitGenerateQuestionsMutation();
  const { data, loading: questionSubtypesLoading } = useQuestionSubtypesQuery({
    variables: {
      questionType: [Question_Type.Practical, Question_Type.Theoretical, Question_Type.MultipleChoiceQuestions],
    },
  });

  const questionSubtypesData = data?.questionSubtypes ? getQuestionSubtypesData(data.questionSubtypes) : {};
  const questionSubtypes = useMemo(() => getQuestionSubtypesOptions(questionSubtypesData), [questionSubtypesData]);
  const scopeGroups = useMemo(() => getScopeGroups({ competencyMap, selectParts }), [competencyMap, selectParts]);
  const scopeAreasCount = useMemo(() => getScopeAreasCount(scopeGroups), [scopeGroups]);

  const form = useForm<GenerateQuestionsForCMModalForm>({
    defaultValues: {
      grade: Grade.Middle,
      questionSubtypes: {},
      language: Language.Ru,
      amount: "1",
      comment: "",
      criterionComment: "",
    },
  });

  const {
    handleSubmit,
    setError,
    clearErrors,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = form;

  const watchedGrade = watch("grade");
  const watchedAmount = watch("amount");
  const watchedLanguage = watch("language");
  const watchedQuestionSubtypes = watch("questionSubtypes");
  const selectedSubtypeKeys = Object.entries(watchedQuestionSubtypes || {})
    .filter(([, value]) => value)
    .map(([key]) => key);

  const totalQuestionsCount = getTotalQuestionsCount({
    areasCount: scopeAreasCount,
    selectedTypesCount: selectedSubtypeKeys.length,
    amount: watchedAmount,
  });

  const validateQuestionSubtypesInputs = (formData: GenerateQuestionsForCMModalForm) => {
    const checkedQuestionSubtypesEntries = Object.entries(formData.questionSubtypes).filter(([, value]) => value);

    if (checkedQuestionSubtypesEntries.length === 0 || checkedQuestionSubtypesEntries.length > 3) {
      setError("forDisplaySubtypesErrors", {
        type: "required",
        message: "Необходимо выбрать от 1 до 3 типов вопросов",
      });

      return null;
    }

    clearErrors("forDisplaySubtypesErrors");
    return checkedQuestionSubtypesEntries;
  };

  const handleToggleSubtype = (value: string) => {
    const formData = getValues();
    const isSelected = Boolean(formData.questionSubtypes[value]);
    const checkedCount = Object.values(formData.questionSubtypes).filter(Boolean).length;

    if (!isSelected && checkedCount >= 3) return;

    const nextQuestionSubtypes = {
      ...formData.questionSubtypes,
      [value]: !isSelected,
    };

    setValue("questionSubtypes", nextQuestionSubtypes);
    validateQuestionSubtypesInputs({
      ...formData,
      questionSubtypes: nextQuestionSubtypes,
    });
  };

  const onSubmit = (formData: GenerateQuestionsForCMModalForm) => {
    if (!competencyMap?.id || !questionSubtypesData) return;

    const checkedQuestionSubtypesEntries = validateQuestionSubtypesInputs(formData);

    if (!checkedQuestionSubtypesEntries) return;

    generateQuestions({
      variables: {
        input: {
          competencyMapId: competencyMap.id,
          grade: formData.grade,
          data: checkedQuestionSubtypesEntries.map(([key]) => questionSubtypesData[key]),
          language: formData.language,
          amount: Number(formData.amount),
          selectParts,
        },
      },
    })
      .then(() => {
        addToast({ type: ToastTypeEnum.SUCCESS, text: "Генерация вопросов может занять несколько минут" });
        refetchCompetencyMapGenQuestionsLog();
        onSuccess?.();
        onClose();
      })
      .catch((e) => parseApolloError(e, { addToast }));
  };

  const selectedGradeDescription = watchedGrade ? GRADE_DESCRIPTIONS[watchedGrade] : GRADE_DESCRIPTIONS.Middle;
  const footerMetaText = getFooterMetaText({
    areasCount: scopeAreasCount,
    selectedTypesCount: selectedSubtypeKeys.length,
    amount: watchedAmount,
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: 2.25,
          overflow: "hidden",
          boxShadow: "0 28px 72px rgba(15, 23, 42, 0.22)",
          py: 2,
          px: 3,
        },
      }}>
      {questionSubtypesLoading ? (
        <CircularLoading />
      ) : (
        <>
          <ModalHeader
            title={competencyMap?.title}
            onClose={onClose}
          />
          <DialogContent sx={{ mt: 2 }}>
            <Form form={form}>
              <Stack spacing={1.25}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.25}
                  justifyContent='flex-end'
                  alignItems='stretch'>
                  {!hideScopeAndPrompts && <ScopeSection scopeGroups={scopeGroups} />}
                  <TotalQuestionsCard
                    totalQuestionsCount={totalQuestionsCount}
                    areasCount={scopeAreasCount}
                    selectedTypesCount={selectedSubtypeKeys.length}
                    amount={watchedAmount}
                  />
                </Stack>
                <Grid
                  container
                  gap={1}
                  alignItems='stretch'
                  sx={{ flexWrap: { xs: "wrap", md: "nowrap" } }}>
                  <Grid
                    item
                    xs={12}
                    md={4}>
                    <SettingsSection
                      grade={watchedGrade}
                      amount={watchedAmount}
                      language={watchedLanguage}
                      gradeDescription={selectedGradeDescription}
                      onGradeChange={(grade) => setValue("grade", grade)}
                      onAmountChange={(amount) => setValue("amount", amount)}
                      onLanguageChange={(language) => setValue("language", language)}
                      getGradeButtonColors={getGradeButtonColors}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={8}>
                    <QuestionTypesSection
                      questionSubtypes={questionSubtypes}
                      selectedSubtypeKeys={selectedSubtypeKeys}
                      watchedQuestionSubtypes={watchedQuestionSubtypes || {}}
                      subtypesError={errors.forDisplaySubtypesErrors?.message}
                      onToggleSubtype={handleToggleSubtype}
                    />
                  </Grid>
                </Grid>

                {!hideScopeAndPrompts && (
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1.25}>
                    <PromptSection
                      name='comment'
                      title='Инструкции к вопросам'
                      placeholder='Фокус на практике, избегать определений, кейсы из доменной области...'
                    />
                    <PromptSection
                      name='criterionComment'
                      title='Инструкции к критериям оценки'
                      placeholder='На что делать акцент при оценке, какие сигналы считать сильными...'
                    />
                  </Stack>
                )}
              </Stack>
            </Form>
          </DialogContent>
          <ModalFooter
            footerMetaText={footerMetaText}
            loading={loading}
            disabled={!selectedSubtypeKeys.length}
            onClose={onClose}
            onSubmit={handleSubmit(onSubmit)}
          />
        </>
      )}
    </Dialog>
  );
};

export default GenerateQuestionsForCMModal;
